import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Calendar, ImageIcon, Search, Download, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from "recharts";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-[#E2E8F0] p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-mono tracking-widest text-[#475569]">{label.toUpperCase()}</p>
      <Icon size={16} className={accent ? "text-[#5B4CFF]" : "text-[#475569]"} />
    </div>
    <p className="mt-4 font-display text-3xl md:text-4xl font-extrabold text-[#0F172A] tracking-tighter">
      {value?.toLocaleString?.() ?? value}
    </p>
  </div>
);

export default function AdminOverview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((res) => setData(res.data)).catch(() => {});
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 md:py-12 max-w-7xl">
      <div className="mb-8">
        <p className="kicker mb-2">DASHBOARD</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tighter">Overview</h1>
        <p className="mt-2 text-sm text-[#475569]">Real-time platform metrics across all events.</p>
      </div>

      {data ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <Stat icon={Calendar} label="Total events" value={data.total_events} accent />
            <Stat icon={TrendingUp} label="Published" value={data.published_events} accent />
            <Stat icon={ImageIcon} label="Total photos" value={data.total_photos} />
            <Stat icon={Search} label="Total searches" value={data.total_searches} />
            <Stat icon={Download} label="Total downloads" value={data.total_downloads} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-[#E2E8F0] p-6" data-testid="chart-searches">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <p className="kicker mb-1">SEARCHES</p>
                  <h3 className="font-display text-lg font-semibold text-[#0F172A]">Last 7 days</h3>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.searches_last_7_days} margin={{ top: 10, right: 0, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#475569", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                  <YAxis tick={{ fill: "#475569", fontSize: 11 }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#F8FAFC" }} contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 0 }} />
                  <Bar dataKey="searches" fill="#5B4CFF" radius={[0, 0, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white border border-[#E2E8F0] p-6" data-testid="popular-events">
              <p className="kicker mb-1">POPULAR EVENTS</p>
              <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-6">By search count</h3>
              {data.popular_events.length === 0 ? (
                <p className="text-sm text-[#475569]">No search data yet.</p>
              ) : (
                <ul className="space-y-4">
                  {data.popular_events.map((ev, idx) => (
                    <li key={ev.event_id} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs text-[#5B4CFF]">0{idx + 1}</span>
                        <span className="text-sm font-medium text-[#0F172A] truncate">{ev.event_name}</span>
                      </div>
                      <span className="text-xs font-mono text-[#475569] shrink-0">{ev.searches}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : (
        <p className="text-sm text-[#475569] font-mono">LOADING…</p>
      )}
    </div>
  );
}
